/**
 * Block 13 PR-G Phase 6.5B Amendment A1 — currency word substitution в template prose.
 *
 * **Контекст бага.** Templates (kz.ru.json, kz.kk.json, ru.ru.json, uz.ru.json,
 * uz.uz.json) содержат hardcoded currency word в text section paragraphs
 * (например «составляет _________________ тенге» в kz.ru). Ранее template
 * был source of truth для currency, поэтому hardcoded word совпадал с
 * `template.meta.currencyCode`.
 *
 * **Phase 6.5B Amendment A1** разделил template language от currency: user
 * может оформить kz.ru (Russian-language) договор в RUB (RU client) или UZS
 * (UZ client). Без substitution PDF выводит «100 000 (сто тысяч) тенге» при
 * draft.currencyCode='RUB' — legally inconsistent (cost line says RUB symbol
 * ₽, prose says тенге).
 *
 * **Решение.** Pure function `replaceCurrencyWordsInText` сканирует text за
 * template-currency-word (per `template.meta.lang` + `template.meta.currencyCode`)
 * и заменяет на draft-currency-appropriate word в том же language. Если
 * draft.currencyCode === template.meta.currencyCode → no-op (oригинальный
 * word уже корректен).
 *
 * Mapping `CURRENCY_WORD_BY_LANG_CODE[lang][code]` фиксирует grammatically
 * correct genitive-amount forms (Russian «рублей» genitive plural, kk/uz
 * indeclinable):
 *   - ru: { KZT: 'тенге', RUB: 'рублей', UZS: 'сум' }
 *   - kk: { KZT: 'теңге', RUB: 'рубль', UZS: 'сум' }
 *   - uz: { KZT: 'tenge', RUB: 'rubl', UZS: "so'm" }
 *
 * Reference:
 *   - ADR-0025 Amendment A1 (2026-05-24)
 *   - Templates: `apps/web/src/features/contract/templates/{kz.ru,kz.kk,ru.ru,uz.ru,uz.uz}.json`
 *   - Reviewer subagent HIGH issue 2026-05-24 (currency word desync)
 */

import type { ContractCurrencyCode, ContractLanguage } from '@pomoshnik/shared';

/**
 * Per-language currency words в genitive-amount form («в размере 100 000 X»).
 * Russian RUB uses «рублей» (genitive plural); other locales' currencies either
 * indeclinable (KZT/UZS) или nominative (kk/uz RUB).
 */
const CURRENCY_WORD_BY_LANG_CODE: Record<ContractLanguage, Record<ContractCurrencyCode, string>> = {
  ru: { KZT: 'тенге', RUB: 'рублей', UZS: 'сум' },
  kk: { KZT: 'теңге', RUB: 'рубль', UZS: 'сум' },
  uz: { KZT: 'tenge', RUB: 'rubl', UZS: "so'm" },
};

/** Escape regex special chars в literal string (apostrophe для so'm). */
function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Заменяет all occurrences of template's original currency word на target
 * currency word в том же language. Word-boundary aware (Cyrillic + Latin):
 * `(^|[^\p{L}])word(?![\p{L}])` — match только если перед word нет буквы и
 * после word нет буквы.
 *
 * Защита: «Танганика тенгеренки» не матчит «тенге» substring, «Sso'm» не
 * матчит «so'm» (буква S перед).
 *
 * No-op если draftCode === templateCode (template original word уже соответствует).
 *
 * @param text — paragraph string (после substituteUnderscores)
 * @param draftCode — user-selected currency code (`ContractDraft.currencyCode`)
 * @param templateLang — template language code (`template.meta.lang`)
 * @param templateCode — template's original currency code (`template.meta.currencyCode`)
 * @returns text с replaced currency words (или original text при no-op)
 */
export function replaceCurrencyWordsInText(
  text: string,
  draftCode: ContractCurrencyCode,
  templateLang: ContractLanguage,
  templateCode: ContractCurrencyCode,
): string {
  if (draftCode === templateCode) return text;
  const fromWord = CURRENCY_WORD_BY_LANG_CODE[templateLang]?.[templateCode];
  const toWord = CURRENCY_WORD_BY_LANG_CODE[templateLang]?.[draftCode];
  // Defensive guard: если template/draft содержит code/lang outside enum
  // (corrupt data load) — return text unchanged вместо crypt TypeError.
  // Per reviewer Phase 6.5B 2026-05-24 medium #1.
  if (!fromWord || !toWord) return text;
  // (^|non-letter) capture + word + negative lookahead non-letter.
  // `u` flag нужен для \p{L} Unicode property.
  const pattern = new RegExp(`(^|[^\\p{L}])${escapeForRegex(fromWord)}(?![\\p{L}])`, 'gu');
  return text.replace(pattern, `$1${toWord}`);
}
